import React, { useMemo } from 'react'
import { ICycleModule } from 'pages/Planning/types/cycleSession'
import { ICycleSession } from 'pages/Planning/types/cycleSession'
import { createColorMap } from 'pages/Planning/utils/chartColors'
import './styles.css'

interface DisciplinesDistributionProps {
  modules: ICycleModule[]
  sessions: ICycleSession[]
  isDarkMode?: boolean
}

interface DisciplineData {
  moduleId: string
  subject: string
  totalHours: number
  sessionsCount: number
  color: string
  order: number
}

/**
 * Componente que exibe distribuição total do ciclo agrupada por disciplina
 * Inclui gráfico de barras horizontal e lista de disciplinas
 */
const DisciplinesDistribution: React.FC<DisciplinesDistributionProps> = ({
  modules,
  sessions,
  isDarkMode = false,
}) => {
  // Cria mapa de cores baseado nos IDs dos módulos
  const colorMap = useMemo(() => {
    const moduleIds = modules.map((m) => String(m._id))
    return createColorMap(moduleIds)
  }, [modules])

  // Determina a ordem de aparição de cada módulo no ciclo
  const moduleOrderMap = useMemo(() => {
    const orderMap = new Map<string, number>()
    const seenModules = new Set<string>()

    sessions.forEach((session) => {
      const moduleId = session.module?._id
      if (moduleId && !seenModules.has(moduleId)) {
        seenModules.add(moduleId)
        orderMap.set(moduleId, session.order)
      }
    })

    return orderMap
  }, [sessions])

  // Agrega dados dos módulos e ordena pela ordem de aparição no ciclo
  const disciplinesData = useMemo(() => {
    return modules
      .map((module) => ({
        moduleId: module._id,
        subject: module.subject,
        totalHours: module.hours,
        sessionsCount: module.sessions,
        color: colorMap.get(module._id) || '#3366CC',
        order: moduleOrderMap.get(module._id) || 999,
      }))
      .sort((a, b) => a.order - b.order)
  }, [modules, colorMap, moduleOrderMap])

  // Calcula o total de horas para calcular porcentagens
  const totalHours = useMemo(() => {
    return disciplinesData.reduce((sum, d) => sum + d.totalHours, 0)
  }, [disciplinesData])

  return (
    <div
      className={`cycle-disciplines-distribution ${isDarkMode ? 'dark' : 'light'}`}
      data-tour="dashboard-disciplines-chart"
    >
      <h3 className="cycle-disciplines-title">Distribuição por disciplina</h3>

      {/* Gráfico de Barras Horizontal */}
      <div className="cycle-disciplines-chart">
        <div className="cycle-bar-chart-container">
          {disciplinesData.map((discipline) => {
            const percentage = totalHours > 0 ? (discipline.totalHours / totalHours) * 100 : 0
            const showValueInside = percentage > 8
            return (
              <div
                key={discipline.moduleId}
                className="cycle-bar-chart-segment"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: discipline.color,
                }}
                title={discipline.subject}
              >
                {showValueInside && (
                  <div className="cycle-bar-chart-value-inside">
                    {discipline.totalHours.toFixed(1)}h
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Lista de Disciplinas */}
      <div className="cycle-disciplines-list">
        {disciplinesData.map((discipline, index) => (
          <div key={discipline.moduleId} className="cycle-discipline-item">
            <div className="cycle-discipline-icon" style={{ backgroundColor: discipline.color }} />
            <span className="cycle-discipline-name">{discipline.subject}</span>
            <div className="cycle-discipline-duration">{discipline.totalHours.toFixed(0)}h</div>
            <div className="cycle-discipline-duration">{discipline.sessionsCount} sessões</div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default DisciplinesDistribution
